import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/SaveAlt";

import TagConfig from "..";
import { updateItem as updateItemOp } from "store/operations";
import ISXUtils, {
  ErrorProvider,
  ErrorContext,
  AlertsAddressBookContext,
} from "services/Utils";
import { default as _head } from "lodash/head";
import { default as _set } from "lodash/set";
import { default as _isEmpty } from "lodash/isEmpty";

const SaveButton = (props) => {
  const errorContext = useContext(ErrorContext);

  return (
    <Button
      sx={{ marginLeft: 0.375, marginRight: 0.375 }}
      color="primary"
      onClick={props.save}
      startIcon={<SaveIcon />}
      disabled={Object.values(errorContext.errors).some(Boolean)}
    >
      Save
    </Button>
  );
};

const TagConfigDialog = (props) => {
  const [tagProperties, setTagProperties] = useState({});
  const [dirty, setDirty] = useState({});
  const [warningToastOpen, setWarningToastOpen] = useState(false);
  const [confirmToastOpen, setConfirmToastOpen] = useState(false);
  const stacks = useSelector((state) => state.isx.stacks);
  const lastTagId = useRef(null);

  const [sendTo, setSendTo] = useState([]);
  const value = useMemo(() => ({ sendTo, setSendTo }), [sendTo]);

  const dispatch = useDispatch();

  const { open, setOpen, tag } = props;

  const stack = stacks?.[tag?.stack];

  useEffect(() => {
    if (tag && tag.guuid !== lastTagId.current) {
      setTagProperties(stack.data_config?.[tag.attribute] ?? {});
      lastTagId.current = tag.guuid;
    }
  }, [stack.data_config, tag]);

  const handleClose = () => {
    if (Object.keys(dirty).length === 0) {
      setOpen(false);
    } else {
      setWarningToastOpen(true);
    }
  };

  const handleTagPropChange = (tagId, prop, value) => {
    const propPath = prop.split(".");
    const headProp = _head(propPath);
    setTagProperties((tagProperties) => {
      return _set({ ...tagProperties }, propPath, value);
    });
    setDirty((dirty) => {
      return { ...dirty, [headProp]: true };
    });
  };

  const handleTagPropsChange = (tagId, tagProps) => {
    setTagProperties((tagProperties) => {
      return {
        ...tagProperties,
        ...tagProps,
      };
    });
    setDirty((dirty) => {
      const dirtyProps = Object.keys(tagProps).reduce((acc, p) => {
        acc[p] = true;
        return acc;
      }, {});
      return { ...dirty, ...dirtyProps };
    });
  };

  const updateItem = (guuid, contents) => {
    dispatch(updateItemOp(guuid, contents));
  };

  async function saveTagPropertiesToDB() {
    const tagAttributeEscaped = ISXUtils.escapeTagPeriods(tag.attribute);
    const update = Object.entries(dirty).reduce((acc, [prop, flag]) => {
      if (flag) {
        const val = (tagProperties ?? {})[prop];
        const key = `data_config.${tagAttributeEscaped}.${prop}`;
        acc[key] = val;
      }
      return acc;
    }, {});

    if (!_isEmpty(update)) {
      //   console.log("UPDATE", stack.guuid, update);
      await updateItem(stack.guuid, update);
    }
    setDirty({});
    setConfirmToastOpen(true);
  }

  const reset = () => {
    setTagProperties(stack.data_config?.[tag.attribute] ?? {});
    setDirty({});
  };

  if (tag) {
    const label =
      (tag &&
        stack &&
        (stack?.name || JSON.parse(stack.dev_session ?? "{}")["stack.label"]) +
          ":" +
          ISXUtils.getTagDisplayText(tag.attribute, stack.data_config)) ||
      "";

    return (
      <ErrorProvider>
        <AlertsAddressBookContext.Provider value={value}>
          <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{ sx: { width: "100%", maxWidth: 960 } }}
          >
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                {label}
                <IconButton onClick={handleClose} size="large">
                  <ClearIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                paddingTop: 0,
                paddingLeft: 4.5,
                paddingRight: 4.5,
              }}
            >
              <TagConfig
                stack={stack}
                tagId={tag.attribute}
                tagProperties={tagProperties}
                handleTagPropChange={handleTagPropChange}
                handleTagPropsChange={handleTagPropsChange}
                showCheck={false}
                readOnly={false}
              />
            </DialogContent>
            <DialogActions>
              {Object.values(dirty).some(Boolean) ? (
                <>
                  <SaveButton save={saveTagPropertiesToDB} />
                  <Tooltip
                    title="Current changes will be discarded"
                    placement="top"
                  >
                    <Button
                      sx={{
                        marginLeft: 0.375,
                        marginRight: 0.375,
                        color: "red",
                      }}
                      onClick={reset}
                    >
                      Cancel
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <Button
                  sx={{
                    marginLeft: 0.375,
                    marginRight: 0.375,
                    color: "red",
                  }}
                  onClick={handleClose}
                >
                  Close
                </Button>
              )}
            </DialogActions>
            <Snackbar
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              open={warningToastOpen}
              autoHideDuration={3000}
              onClose={() => setWarningToastOpen(false)}
              message="Updates not saved. Please save or cancel."
            />
            <Snackbar
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              open={confirmToastOpen}
              autoHideDuration={3000}
              onClose={() => setConfirmToastOpen(false)}
              message="Updated tag properties saved"
            />
          </Dialog>
        </AlertsAddressBookContext.Provider>
      </ErrorProvider>
    );
  } else {
    return null;
  }
};

export default TagConfigDialog;
